import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import LogoFallback from "/img/logo.png";
import parse from 'html-react-parser';

export default function CareerPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Career | University of Creative Technology Chittagong",
}) {
	const [careers, setCareers] = useState([]);
	const [filtered, setFiltered] = useState([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [lcareer, setLcareer] = useState(null);
	const fetchCareers = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/career", payload);
			
			
			// Loop data (array)
			setCareers(response.data || []);
			setFiltered(response.data || []);
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	const latestCareer = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/latest-career", payload);
			
			setLcareer(response.data[0]);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {
		fetchCareers();
		latestCareer();
	}, []);
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  // Search filter
  useEffect(() => {
    let result = careers;
    if (search) {
      result = careers.filter(
        (c) =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.department.toLowerCase().includes(search.toLowerCase()) ||
          c.location.toLowerCase().includes(search.toLowerCase())
      );
    }
    setFiltered(result);
  }, [search, careers]);
  
	function textformat(textdata){
		let textinfo="";
		textinfo=textdata?.replace(/\\n/g, '<br />');
		textinfo=textinfo?.split('\\n').join('\n');
		textinfo=textinfo?.split('\n').join('<br />');
		return textinfo;
	}
  return (
    <div className="relative bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF] overflow-hidden">
		<section className="relative py-20 text-center overflow-hidden">
        <div className="absolute inset-0 bg-[#fff8e1] opacity-40 -skew-y-3"></div>
        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-4xl md:text-5xl font-bold text-[#7c5b0e]"
        >
          Career  <span className="text-gray-700">Opportunities</span>
        </motion.h1>
        <p className="relative z-10 mt-4 text-gray-700 max-w-2xl mx-auto">
          Join our team and shape your future with us.
        </p>
		{/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 -mt-6">
          <svg
            viewBox="0 0 1440 80"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-20"
          >
            <path
              d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z"
              fill="#014864"
              opacity="0.92"
            />
          </svg>
        </div>
      </section>
	  
      {/* Career Table Section */}
      <section className="max-w-7xl mx-auto py-16 px-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Available Positions</h2>
          <input
            type="text"
            placeholder="Search careers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg px-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Loading careers...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : filtered.length > 0 ? (
          <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
            <table className="min-w-full table-auto">
              <thead className="bg-[#014864] text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Title</th>
                  <th className="px-6 py-3 text-left">Department</th>
                  <th className="px-6 py-3 text-left">Specialization</th>
				  <th className="px-6 py-3 text-left">Opening Date</th>
                  <th className="px-6 py-3 text-left">Deadline</th>
                  <th className="px-6 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((career) => (
                  <tr
                    key={career.id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-6 py-4">{career.designation_name}</td>
                    <td className="px-6 py-4">{career.department_name}</td>
                    <td className="px-6 py-4">{career.subject && career.subject.trim() !== "" ? career.subject : "Relevant"}</td>
					<td className="px-6 py-4">
                      {new Date(career.open_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(career.end_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Link
                        to={`/career-details/${career.id}`}
                        className="bg-[#014864] text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500">No careers found.</p>
        )}
      </section>

      {/* Contact Section */}
      <section className="max-w-6xl mx-auto py-16 px-6 grid md:grid-cols-3 gap-8 text-center">
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Mail className="mx-auto w-10 h-10 text-[#014864] mb-4" />
          <h3 className="font-bold text-lg">Email</h3>
          <p className="text-gray-600">info@uctc.edu.bd</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Phone className="mx-auto w-10 h-10 text-[#014864] mb-4" />
          <h3 className="font-bold text-lg">Phone</h3>
          <p className="text-gray-600">+8801707508080</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <MapPin className="mx-auto w-10 h-10 text-[#014864] mb-4" />
          <h3 className="font-bold text-lg">Office Address</h3>
          <p className="text-gray-600">Chandgaon Police Station, 1084, Shah Amanat Bridge Connecting Road, Chittagong 4212</p>
        </div>
      </section>
    </div>
  );
}
